import { Icon } from "@sellar-io/components";
import { formatCurrency, pluralize } from "@sellar-io/common";
import { UnifiedCheckoutSuccessOrder } from "../hooks/useUnifiedCheckoutSuccessPageData";
import { useNavigate } from "react-router";
import { ORDERS } from "~/constants/routes";
import { UnifiedCheckoutOrderPaymentStatusContent } from "./UnifiedCheckoutOrderPaymentStatus";
import type { ReactNode } from "react";
import { twMerge } from "tailwind-merge";
import { useUnifiedCheckoutOrderPaymentStatus } from "../hooks/useUnifiedCheckoutOrderPaymentStatus";

type UnifiedCheckoutOrderDisplayProps = {
  orders: UnifiedCheckoutSuccessOrder[];
  children?: ReactNode;
};

const UnifiedCheckoutOrderDisplayRow = ({
  order,
}: {
  order: UnifiedCheckoutSuccessOrder["order"];
}) => {
  const navigate = useNavigate();
  const paymentState = useUnifiedCheckoutOrderPaymentStatus(order);

  return (
    <div
      className={twMerge(
        "flex flex-col gap-1 px-4",
        paymentState.isFailed && "bg-red-50",
      )}
    >
      <button
        type="button"
        className="flex items-center gap-3 rounded-md px-2 py-2 text-left hover:bg-zinc-50"
        onClick={() => navigate(`${ORDERS}/${order.id}?tab=All+orders`)}
      >
        {order.Supplier.Business.logoUrl ? (
          <img
            src={order.Supplier.Business.logoUrl}
            alt=""
            className="size-7 rounded-full object-cover"
          />
        ) : (
          <div className="size-7 rounded-full bg-zinc-200" />
        )}
        <div className="min-w-0 flex-1">
          <p className="truncate text-base font-semibold">
            {order.Supplier.Business.name}
          </p>
          <p className="text-base font-normal text-zinc-500">
            {order.OrderItems.length}{" "}
            {pluralize("item", order.OrderItems.length)}
          </p>
        </div>
        <div className="flex shrink-0 flex-col items-end">
          {!paymentState.isFailed && (
            <p
              role="status"
              className={twMerge(
                paymentState.isPending ? "text-amber-700" : "text-emerald-600",
              )}
            >
              {paymentState.isPending ? "Payment pending" : "Payment sent"}
            </p>
          )}
          <p className="text-sm font-medium">
            {formatCurrency(order.price, order.currency)}
          </p>
        </div>
      </button>
      {paymentState.isFailed && (
        <UnifiedCheckoutOrderPaymentStatusContent
          order={order}
          paymentState={paymentState}
        />
      )}
    </div>
  );
};

export const UnifiedCheckoutOrderDisplay = ({
  orders,
  children,
}: UnifiedCheckoutOrderDisplayProps) => {
  return (
    <div className="overflow-hidden rounded-2xl border border-zinc-200 bg-white shadow-sm">
      <div className="flex items-center gap-3 bg-emerald-50 p-4">
        <div className="flex size-10 shrink-0 items-center justify-center rounded-full bg-emerald-100 text-emerald-700">
          <Icon icon="CHECK" size={0.9} />
        </div>
        <div>
          <h1 className="font-semibold text-zinc-900">
            {pluralize("Order", orders.length)} placed
          </h1>
          <p className="text-base font-normal text-zinc-600">
            {orders.reduce(
              (count, { order }) =>
                count +
                order.OrderItems.reduce(
                  (itemCount, item) => itemCount + item.quantity,
                  0,
                ),
              0,
            )}{" "}
            items · {orders.length} {pluralize("supplier order", orders.length)}
            {" · "}
            {formatCurrency(
              orders.reduce((total, { order }) => total + order.price, 0),
              orders[0]?.order.currency ?? "gbp",
            )}{" "}
            total
          </p>
        </div>
      </div>
      <div className="flex flex-col gap-1 py-4">
        <p className="mb-1 px-4 text-sm font-semibold text-zinc-500">
          Your orders
        </p>
        {orders.map(({ order }) => (
          <UnifiedCheckoutOrderDisplayRow key={order.id} order={order} />
        ))}
        <div className="flex w-full flex-col gap-1 px-4">{children}</div>
      </div>
    </div>
  );
};
